import React, { Suspense, lazy, useEffect } from 'react';
import { createBrowserRouter, createMemoryRouter, Navigate, Outlet, useParams, useLocation, useOutlet, useRouteError } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { isMemberUnpaidOrExpired } from '../firebase';
import { GlobalLayout } from '../components/Layout/GlobalLayout';
import { ErrorBoundary } from 'react-error-boundary';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { usePostHog } from '@posthog/react';
import { initLobbyPresence, clearLobbyPresence, updateLobbyPresenceProfile } from '../arena/state/ArenaFriendsService';
import { PermissionKey, canAccessAdmin as checkCanAccessAdmin } from '../utils/permissions';
import { arenaLogger } from '../arena/utils/arenaLogger';

const PostHogPageView = () => {
  const location = useLocation();
  const posthog = usePostHog();
  useEffect(() => {
    try {
      posthog?.capture('$pageview', { $current_url: window.location.href });
    } catch {
      // Ignore analytics failures caused by ad blockers or network issues
    }
  }, [location, posthog]);
  return null;
};

const GlobalPresenceWatcher = () => {
  const { user, profile } = useAuth();
  const prevUidRef = React.useRef<string | null>(null);

  const name = profile?.displayName || profile?.nickname || user?.displayName || 'Card Shark';
  const photo = profile?.photoUrl || profile?.photoURL || profile?.avatarUrl || user?.photoURL || '';

  // 1. Maintain presence lifecycle tied strictly to user.uid
  useEffect(() => {
    if (!user?.uid) {
      if (prevUidRef.current) {
        clearLobbyPresence(prevUidRef.current);
        prevUidRef.current = null;
      }
      return;
    }

    prevUidRef.current = user.uid;
    const teardown = initLobbyPresence(user.uid, name, photo);
    return () => {
      teardown();
    };
  }, [user?.uid]);

  // 2. Seamlessly update profile details without tearing down presence connection
  useEffect(() => {
    if (!user?.uid) return;
    updateLobbyPresenceProfile(user.uid, name, photo);
  }, [user?.uid, name, photo]);

  return null;
};

const RootLayout = () => (
  <>
    <PostHogPageView />
    <GlobalPresenceWatcher />
    <Outlet />
  </>
);

// Fallback loader
const PageLoader = () => (
  <div role="status" aria-live="polite" className="flex flex-col items-center justify-center min-h-screen w-full bg-[#070a10]">
    <Loader2 className="animate-spin text-red-600 mb-4" size={32} />
    <span className="text-white/40 text-xs font-medium uppercase tracking-widest">Loading...</span>
  </div>
);

export const isChunkLoadError = (err: any): boolean => {
  if (!err) return false;
  const name = (err.name || '').toString().toLowerCase();
  const msg = (err.message || err.toString() || '').toLowerCase();
  return (
    name.includes('chunk') ||
    msg.includes('chunk') ||
    msg.includes('loading css chunk') ||
    msg.includes('loading chunk') ||
    msg.includes('css chunk') ||
    msg.includes('failed to fetch dynamically imported module') ||
    msg.includes('importing a module script failed') ||
    msg.includes('error loading dynamically imported module') ||
    /css chunk [^\s]+ failed/i.test(msg) ||
    /loading (css )?chunk/i.test(msg)
  );
};

// Fallback error
const RouteErrorFallback = ({ error, resetErrorBoundary }: any) => {
  console.error('[RouteErrorFallback] Caught unhandled route error:', error);
  try {
    arenaLogger.error(
      `[RouteErrorFallback] ${error?.message || error}`,
      error?.stack ? { stack: error.stack } : undefined,
      'REACT_CRASH'
    );
  } catch {}
  const isChunkError = isChunkLoadError(error);
  const message = error?.message || "A network error or update caused this page to fail.";

  const handleAction = () => {
    if (isChunkError) {
      window.location.reload();
    } else if (resetErrorBoundary) {
      resetErrorBoundary();
    } else {
      window.location.reload();
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen w-full text-center p-6 bg-[#070a10]">
      <AlertTriangle className="text-red-500 mb-4" size={48} />
      <h2 className="text-xl font-bold text-white mb-2">
        {isChunkError ? "App Update Ready" : "Failed to Load Page"}
      </h2>
      <p className="text-white/60 text-sm mb-6 max-w-md">
        {isChunkError
          ? "A new version of ATL Card Sharks was deployed. Please tap reload to apply the latest update."
          : message}
      </p>
      <button
        onClick={handleAction}
        className="px-6 py-2.5 bg-[#c72f38] hover:bg-red-500 text-white font-bold rounded-xl border-none cursor-pointer shadow-lg transition-colors"
      >
        {isChunkError ? "Reload Now" : "Try Again"}
      </button>
    </div>
  );
};

const LazyRoute = ({ children }: { children: React.ReactNode }) => {
  return (
    <ErrorBoundary FallbackComponent={RouteErrorFallback}>
      <Suspense fallback={<PageLoader />}>
        {children}
      </Suspense>
    </ErrorBoundary>
  );
};

const PublicLayout = () => {
  const outlet = useOutlet();
  
  return (
    <div style={{ width: '100%', minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: '#070a10' }}>
      {outlet}
    </div>
  );
};

const lazyWithRetry = (factory: any, maxRetries = 2, initialDelay = 300) => {
  return lazy(() => {
    let attempts = 0;
    const load = (): Promise<any> => factory().catch((err: any) => {
      attempts += 1;
      if (attempts <= maxRetries) {
        const delay = initialDelay * attempts;
        return new Promise((resolve) => setTimeout(resolve, delay)).then(load);
      }

      if (isChunkLoadError(err)) {
        // If retry failed, a new version was deployed and chunk hashes changed on the server.
        // Auto-reload once to fetch the latest index.html and fresh chunk manifest.
        const reloadKey = `chunk_reload_${window.location.pathname}`;
        const lastReload = sessionStorage.getItem(reloadKey);
        const now = Date.now();
        if (!lastReload || now - parseInt(lastReload, 10) > 15000) {
          sessionStorage.setItem(reloadKey, now.toString());
          console.warn(`[PWA] Chunk load failed (${err?.message || ''}). Reloading for latest deployment...`);
          window.location.reload();
          return new Promise(() => {}); // Hold until reload
        }
      }
      throw err;
    });
    return load();
  });
};

// Lazy imports
const HomeScreen = lazyWithRetry(() => import("../components/Home/HomeScreen").then(m => ({ default: m.HomeScreen })));
const LoginPage = lazyWithRetry(() => import("../pages/Auth/LoginPage").then(m => ({ default: m.LoginPage })));
const SignUpPage = lazyWithRetry(() => import("../pages/Auth/SignUpPage").then(m => ({ default: m.SignUpPage })));
const ClubHistory = lazyWithRetry(() => import("../pages/Static/ClubHistory").then(m => ({ default: m.ClubHistory })));
const MembersHub = lazyWithRetry(() => import("../pages/Members/MembersHub").then(m => ({ default: m.MembersHub })));
const MembersHubV2 = lazyWithRetry(() => import("../pages/Members/MembersHubV2").then(m => ({ default: m.MembersHubV2 })));
const PublicProfile = lazyWithRetry(() => import("../pages/Members/PublicProfile").then(m => ({ default: m.PublicProfile })));
const UpdateInfo = lazyWithRetry(() => import("../pages/Members/UpdateInfo").then(m => ({ default: m.UpdateInfo })));
const UpdateProfile = lazyWithRetry(() => import("../pages/Members/UpdateProfile").then(m => ({ default: m.UpdateProfile })));
const PointsLedger = lazyWithRetry(() => import("../pages/Members/PointsLedger").then(m => ({ default: m.PointsLedger })));
const SettingsPage = lazyWithRetry(() => import("../pages/Members/SettingsPage").then(m => ({ default: m.SettingsPage })));
const ActivityFeed = lazyWithRetry(() => import("../pages/Feed/ActivityFeed").then(m => ({ default: m.ActivityFeed })));
const ActivityFeedV2 = lazyWithRetry(() => import("../pages/Feed/ActivityFeedV2").then(m => ({ default: m.ActivityFeedV2 })));
const Inbox = lazyWithRetry(() => import("../pages/Messages/Inbox").then(m => ({ default: m.Inbox })));
const ChatThread = lazyWithRetry(() => import("../pages/Messages/ChatThread").then(m => ({ default: m.ChatThread })));
const EventsSummary = lazyWithRetry(() => import("../components/Events/EventsSummary").then(m => ({ default: m.EventsSummary })));
const Directory = lazyWithRetry(() => import("../pages/Members/Directory").then(m => ({ default: m.Directory })));
const EventDetails = lazyWithRetry(() => import("../pages/Events/EventDetails").then(m => ({ default: m.EventDetails })));
const RateEvent = lazyWithRetry(() => import("../pages/Events/RateEvent").then(m => ({ default: m.RateEvent })));
const AppFeedback = lazyWithRetry(() => import("../pages/Static/AppFeedback").then(m => ({ default: m.AppFeedback })));
const LeaderboardPage = lazyWithRetry(() => import("../pages/Members/LeaderboardPage").then(m => ({ default: m.LeaderboardPage })));
const PlayerMatchesHistoryPage = lazyWithRetry(() => import("../pages/Members/PlayerMatchesHistoryPage").then(m => ({ default: m.PlayerMatchesHistoryPage })));
const PlayerPartnershipsPage = lazyWithRetry(() => import("../pages/Members/PlayerPartnershipsPage").then(m => ({ default: m.PlayerPartnershipsPage })));
const PlayerPartnershipDetailPage = lazyWithRetry(() => import("../pages/Members/PlayerPartnershipDetailPage").then(m => ({ default: m.PlayerPartnershipDetailPage })));
const ComprehensiveLeaderboardPage = lazyWithRetry(() => import("../pages/Members/ComprehensiveLeaderboardPage").then(m => ({ default: m.ComprehensiveLeaderboardPage })));
const PlayerRatingAccountingPage = lazyWithRetry(() => import("../pages/Members/PlayerRatingAccountingPage").then(m => ({ default: m.PlayerRatingAccountingPage })));
const MatchDetailPage = lazyWithRetry(() => import("../pages/Members/MatchDetailPage").then(m => ({ default: m.MatchDetailPage })));
const ArenaLobbyPage = lazyWithRetry(() => import("../arena/pages/ArenaLobbyPage").then(m => ({ default: m.ArenaLobbyPage })));
const ArenaTutorialPage = lazyWithRetry(() => import("../arena/pages/ArenaTutorialPage").then(m => ({ default: m.ArenaTutorialPage })));
const ArenaGamePage = lazyWithRetry(() => import("../arena/pages/ArenaGamePage").then(m => ({ default: m.ArenaGamePage })));
const ArenaGameRouteWrapper = () => {
  const { tableId } = useParams<{ tableId: string }>();
  return <ArenaGamePage key={tableId || 'default'} />;
};
const TournamentLobbyPage = lazyWithRetry(() => import("../arena/pages/TournamentLobbyPage").then(m => ({ default: m.TournamentLobbyPage })));
const AchievementsPage = lazyWithRetry(() => import("../pages/Members/AchievementsPage").then(m => ({ default: m.AchievementsPage })));
const GalleryListPage = lazyWithRetry(() => import("../pages/Members/GalleryListPage").then(m => ({ default: m.GalleryListPage })));
const GalleryDetailPage = lazyWithRetry(() => import("../pages/Members/GalleryDetailPage").then(m => ({ default: m.GalleryDetailPage })));
const MemberScorecardPage = lazyWithRetry(() => import("../pages/Members/MemberScorecardPage").then(m => ({ default: m.MemberScorecardPage })));
const LiveScorecardReviewPage = lazyWithRetry(() => import("../pages/Members/LiveScorecardReviewPage").then(m => ({ default: m.LiveScorecardReviewPage })));

// Admin Lazy imports
const AdminDashboardPage = lazyWithRetry(() => import("../pages/Admin/AdminDashboardPage").then(m => ({ default: m.AdminDashboardPage })));
const AdminEventsPage = lazyWithRetry(() => import("../pages/Admin/AdminEventsPage").then(m => ({ default: m.AdminEventsPage })));
const AdminBostonsPage = lazyWithRetry(() => import("../pages/Admin/AdminBostonsPage").then(m => ({ default: m.AdminBostonsPage })));
const AdminLeaderboardPage = lazyWithRetry(() => import("../pages/Admin/AdminLeaderboardPage").then(m => ({ default: m.AdminLeaderboardPage })));
const AdminTournamentsPage = lazyWithRetry(() => import("../pages/Admin/AdminTournamentsPage").then(m => ({ default: m.AdminTournamentsPage })));
const AdminPackagesPage = lazyWithRetry(() => import("../pages/Admin/AdminPackagesPage").then(m => ({ default: m.AdminPackagesPage })));
const AdminMembersPage = lazyWithRetry(() => import("../pages/Admin/AdminMembersPage").then(m => ({ default: m.AdminMembersPage })));
const AdminVenuesPage = lazyWithRetry(() => import("../pages/Admin/AdminVenuesPage").then(m => ({ default: m.AdminVenuesPage })));
const AdminVenueEditPage = lazyWithRetry(() => import("../pages/Admin/AdminVenueEditPage").then(m => ({ default: m.AdminVenueEditPage })));
const AdminSystemPage = lazyWithRetry(() => import("../pages/Admin/AdminSystemPage").then(m => ({ default: m.AdminSystemPage })));
const AdminEventEditPage = lazyWithRetry(() => import("../pages/Admin/AdminEventEditPage").then(m => ({ default: m.AdminEventEditPage })));
const AdminPackageEditPage = lazyWithRetry(() => import("../pages/Admin/AdminPackageEditPage").then(m => ({ default: m.AdminPackageEditPage })));
const AdminCheckInPage = lazyWithRetry(() => import("../pages/Admin/AdminCheckInPage").then(m => ({ default: m.AdminCheckInPage })));
const AdminMemberEditPage = lazyWithRetry(() => import("../pages/Admin/AdminMemberEditPage").then(m => ({ default: m.AdminMemberEditPage })));
const AdminMembershipsPage = lazyWithRetry(() => import("../pages/Admin/AdminMembershipsPage").then(m => ({ default: m.AdminMembershipsPage })));
const AdminMembershipEditPage = lazyWithRetry(() => import("../pages/Admin/AdminMembershipEditPage").then(m => ({ default: m.AdminMembershipEditPage })));
const AdminReportsPage = lazyWithRetry(() => import("../pages/Admin/AdminReportsPage").then(m => ({ default: m.AdminReportsPage })));
const AdminBotFlagsPage = lazyWithRetry(() => import("../pages/Admin/AdminBotFlagsPage").then(m => ({ default: m.AdminBotFlagsPage })));
const RecordMatchPage = lazyWithRetry(() => import("../pages/Admin/RecordMatchPage").then(m => ({ default: m.RecordMatchPage })));
const AdminMatchesListPage = lazyWithRetry(() => import("../pages/Admin/AdminMatchesListPage").then(m => ({ default: m.AdminMatchesListPage })));
const AdminMatchDetailPage = lazyWithRetry(() => import("../pages/Admin/AdminMatchDetailPage").then(m => ({ default: m.AdminMatchDetailPage })));
const AdminFeaturePopupsPage = lazyWithRetry(() => import("../pages/Admin/AdminFeaturePopupsPage").then(m => ({ default: m.AdminFeaturePopupsPage })));
const AdminFeaturePopupEditPage = lazyWithRetry(() => import("../pages/Admin/AdminFeaturePopupEditPage").then(m => ({ default: m.AdminFeaturePopupEditPage })));
const AdminGamesPage = lazyWithRetry(() => import("../pages/Admin/AdminGamesPage").then(m => ({ default: m.AdminGamesPage })));

// Guards
const AuthGuard = () => {
  const { user, profile, loading } = useAuth();
  const location = useLocation();
  if (loading) return null; // Let App.tsx global loader handle this initially
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;

  // Administrators and SuperUsers always have full access to all areas of the app
  if (profile?.isAdmin === true || profile?.isSuper === true) {
    return <Outlet />;
  }

  // Only enforce restrictions if profile is loaded and confirmed unpaid/expired
  if (profile) {
    const isUnpaidOrExpired = isMemberUnpaidOrExpired(profile);
    if (isUnpaidOrExpired) {
      const allowedPaths = [
        '/members',
        '/profile',
        '/messages',
        '/messages/chat',
        '/members/update-info',
        '/members/update-profile'
      ];
      const currentPath = location.pathname;
      const isAllowed = allowedPaths.includes(currentPath) || currentPath.startsWith('/messages/chat');
      if (!isAllowed) {
        return <Navigate to="/members" replace />;
      }
    }
  }

  return <Outlet />;
};

const AdminGuard = () => {
  const { user, profile, loading, canAccessAdmin } = useAuth();
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] bg-[#02040a] text-white">
        <Loader2 className="animate-spin text-red-600 mb-4" size={36} />
        <p className="uppercase tracking-widest text-xs font-black text-white/40">Securing Session...</p>
      </div>
    );
  }

  // Check state profile or cached localStorage profile
  let allowed = canAccessAdmin;
  if (!allowed && user?.uid && typeof window !== 'undefined') {
    try {
      const stored = localStorage.getItem(`acs_profile_${user.uid}`);
      if (stored) {
        const cached = JSON.parse(stored);
        if (checkCanAccessAdmin(cached)) {
          allowed = true;
        }
      }
    } catch (_) {}
  }

  if (!user || !allowed) {
    return <Navigate to="/" replace />;
  }
  return <Outlet />;
};

const PermissionGuard = ({ 
  permission, 
  anyOf, 
  children 
}: { 
  permission?: PermissionKey; 
  anyOf?: PermissionKey[]; 
  children: React.ReactNode 
}) => {
  const { isSuper, hasPermission } = useAuth();
  if (isSuper) return <>{children}</>;

  if (permission && hasPermission(permission)) {
    return <>{children}</>;
  }

  if (anyOf && anyOf.some(p => hasPermission(p))) {
    return <>{children}</>;
  }

  return <Navigate to="/admin" replace />;
};

// Adapters for URL parameters
const EventDetailsRoute = () => {
  const { id } = useParams();
  return <EventDetails key={id || 'default'} eventId={id || ""} />;
};

const RateEventRoute = () => {
  const { id } = useParams();
  return <RateEvent key={id || 'default'} eventId={id || ""} />;
};

const GlobalErrorPage = () => {
  const error: any = useRouteError();
  console.error("Global router error:", error);
  const isChunkError = isChunkLoadError(error);
  const message = error?.message || error?.statusText || "An unexpected error occurred.";

  // Auto-reload on chunk mismatch between client cache and server deployment
  if (isChunkError) {
    const reloadKey = `global_chunk_reload_${window.location.pathname}`;
    const lastReload = sessionStorage.getItem(reloadKey);
    const now = Date.now();
    if (!lastReload || now - parseInt(lastReload, 10) > 15000) {
      sessionStorage.setItem(reloadKey, now.toString());
      window.location.reload();
      return null;
    }
  }
  
  return (
    <div className="flex flex-col items-center justify-center min-h-screen text-center p-6 bg-[#0a0d14] font-sans">
      <AlertTriangle className="text-red-500 mb-4 animate-bounce" size={56} />
      <h1 className="text-2xl font-black text-white mb-2 uppercase tracking-wide">
        {isChunkError ? "App Update Ready" : "Something Went Wrong"}
      </h1>
      <p className="text-white/60 text-sm max-w-md mb-8">
        {isChunkError
          ? "A new version of ATL Card Sharks was deployed. Tap reload to apply the latest update."
          : message}
      </p>
      <div className="flex gap-4 justify-center">
        <button
          onClick={() => window.location.reload()}
          className="px-6 py-2.5 bg-[#c72f38] hover:bg-red-500 text-white font-bold rounded-xl border-none cursor-pointer shadow-lg transition-colors"
        >
          Reload App
        </button>
        <button
          onClick={() => window.location.href = '/'}
          className="px-6 py-2.5 bg-white/10 hover:bg-white/20 text-white font-bold rounded-xl border-none cursor-pointer transition-colors"
        >
          Go Home
        </button>
      </div>
    </div>
  );
};

export const routes = [
  {
    element: <RootLayout />,
    errorElement: <GlobalErrorPage />,
    children: [
  {
    element: <PublicLayout />,
    children: [
      {
        path: "/",
        element: <LazyRoute><HomeScreen /></LazyRoute>
      },
      {
        path: "/login",
        element: <LazyRoute><LoginPage /></LazyRoute>
      },
      {
        path: "/signup",
        element: <LazyRoute><SignUpPage /></LazyRoute>
      }
    ]
  },
  {
    element: <GlobalLayout />,
    children: [
      {
        element: <AuthGuard />,
        children: [
          { path: "/history", element: <LazyRoute><ClubHistory /></LazyRoute> },
          { path: "/members", element: <LazyRoute><MembersHub /></LazyRoute> },
          { path: "/members/hub-v2", element: <LazyRoute><MembersHubV2 /></LazyRoute> },
          { path: "/profile", element: <LazyRoute><MembersHub /></LazyRoute> },
          { path: "/settings", element: <LazyRoute><SettingsPage /></LazyRoute> },
          { path: "/members/update-info", element: <LazyRoute><UpdateInfo /></LazyRoute> },
          { path: "/members/my-info", element: <LazyRoute><UpdateInfo /></LazyRoute> },
          { path: "/my-info", element: <LazyRoute><UpdateInfo /></LazyRoute> },
          { path: "/members/update-profile", element: <LazyRoute><UpdateProfile /></LazyRoute> },
          { path: "/members/points", element: <LazyRoute><PointsLedger /></LazyRoute> },
          { path: "/members/scorecard", element: <LazyRoute><MemberScorecardPage /></LazyRoute> },
          { path: "/members/scorecard/review/:scorecardId", element: <LazyRoute><LiveScorecardReviewPage /></LazyRoute> },
          { path: "/scorecard", element: <LazyRoute><MemberScorecardPage /></LazyRoute> },
          { path: "/achievements", element: <LazyRoute><AchievementsPage /></LazyRoute> },
          { path: "/members/achievements", element: <LazyRoute><AchievementsPage /></LazyRoute> },
          { path: "/members/:id/achievements", element: <LazyRoute><AchievementsPage /></LazyRoute> },
          { path: "/members/:id", element: <LazyRoute><PublicProfile /></LazyRoute> },
          { path: "/profile/:id", element: <LazyRoute><PublicProfile /></LazyRoute> },
          { path: "/feed", element: <LazyRoute><ActivityFeed /></LazyRoute> },
          { path: "/feed-v2", element: <LazyRoute><ActivityFeedV2 /></LazyRoute> },
          { path: "/messages", element: <LazyRoute><Inbox /></LazyRoute> },
          { path: "/messages/chat", element: <LazyRoute><ChatThread /></LazyRoute> },
          { path: "/events", element: <LazyRoute><EventsSummary showHeader={true} /></LazyRoute> },
          { path: "/events/summary", element: <Navigate to="/events" replace /> },
          { path: "/events-summary", element: <Navigate to="/events" replace /> },
          { path: "/events/:id", element: <LazyRoute><EventDetailsRoute /></LazyRoute> },
          { path: "/events/:id/rate", element: <LazyRoute><RateEventRoute /></LazyRoute> },
          { path: "/event/:id", element: <LazyRoute><EventDetailsRoute /></LazyRoute> },
          { path: "/event/:id/rate", element: <LazyRoute><RateEventRoute /></LazyRoute> },
          { path: "/directory", element: <LazyRoute><Directory /></LazyRoute> },
          { path: "/leaderboard", element: <LazyRoute><LeaderboardPage /></LazyRoute> },
          { path: "/leaderboard/:playerId", element: <LazyRoute><PublicProfile /></LazyRoute> },
          { path: "/leaderboard/:playerId/rating", element: <LazyRoute><PlayerRatingAccountingPage /></LazyRoute> },
          { path: "/leaderboard/:playerId/matches", element: <LazyRoute><PlayerMatchesHistoryPage /></LazyRoute> },
          { path: "/leaderboard/:playerId/partnerships", element: <LazyRoute><PlayerPartnershipsPage /></LazyRoute> },
          { path: "/leaderboard/:playerId/partnerships/:partnerId", element: <LazyRoute><PlayerPartnershipDetailPage /></LazyRoute> },
          { path: "/leaderboard/player/:playerId", element: <LazyRoute><PublicProfile /></LazyRoute> },
          { path: "/leaderboard/player/:playerId/rating", element: <LazyRoute><PlayerRatingAccountingPage /></LazyRoute> },
          { path: "/leaderboard/player/:playerId/matches", element: <LazyRoute><PlayerMatchesHistoryPage /></LazyRoute> },
          { path: "/leaderboard/player/:playerId/partnerships", element: <LazyRoute><PlayerPartnershipsPage /></LazyRoute> },
          { path: "/leaderboard/player/:playerId/partnerships/:partnerId", element: <LazyRoute><PlayerPartnershipDetailPage /></LazyRoute> },
          { path: "/members/:id/rating", element: <LazyRoute><PlayerRatingAccountingPage /></LazyRoute> },
          { path: "/profile/:id/rating", element: <LazyRoute><PlayerRatingAccountingPage /></LazyRoute> },
          { path: "/matches/:matchId", element: <LazyRoute><MatchDetailPage /></LazyRoute> },
          { path: "/leaderboard/matches/:matchId", element: <LazyRoute><MatchDetailPage /></LazyRoute> },
          { path: "/leaderboard/advanced", element: <LazyRoute><ComprehensiveLeaderboardPage /></LazyRoute> },
          { path: "/feedback", element: <LazyRoute><AppFeedback /></LazyRoute> },
          { path: "/gallery", element: <LazyRoute><GalleryListPage /></LazyRoute> },
          { path: "/gallery/:albumId", element: <LazyRoute><GalleryDetailPage /></LazyRoute> },
          { path: "/arena", element: <LazyRoute><ArenaLobbyPage /></LazyRoute> },
          { path: "/arena/tutorial", element: <LazyRoute><ArenaTutorialPage /></LazyRoute> },
          { path: "/arena/:tableId", element: <LazyRoute><ArenaGameRouteWrapper /></LazyRoute> },
          { path: "/arena/game/:tableId", element: <LazyRoute><ArenaGameRouteWrapper /></LazyRoute> },
          { path: "/arena/table/:tableId", element: <LazyRoute><ArenaGameRouteWrapper /></LazyRoute> },
          { path: "/arena/watch/:tableId", element: <LazyRoute><ArenaGameRouteWrapper /></LazyRoute> },
          { path: "/arena/:tableId/watch", element: <LazyRoute><ArenaGameRouteWrapper /></LazyRoute> },
          { path: "/play/:tableId", element: <LazyRoute><ArenaGameRouteWrapper /></LazyRoute> },
          { path: "/game/:tableId", element: <LazyRoute><ArenaGameRouteWrapper /></LazyRoute> },
          { path: "/table/:tableId", element: <LazyRoute><ArenaGameRouteWrapper /></LazyRoute> },
          { path: "/watch/:tableId", element: <LazyRoute><ArenaGameRouteWrapper /></LazyRoute> },
          { path: "/room/:tableId", element: <LazyRoute><ArenaGameRouteWrapper /></LazyRoute> },
          { path: "/tournament/:tournamentId", element: <LazyRoute><TournamentLobbyPage /></LazyRoute> },
          { path: "/tournaments/:tournamentId", element: <LazyRoute><TournamentLobbyPage /></LazyRoute> },
          {
            path: "/admin",
            element: <AdminGuard />,
            children: [
              { index: true, element: <LazyRoute><AdminDashboardPage /></LazyRoute> },
              { path: "events", element: <PermissionGuard anyOf={['createEvents', 'editEvents', 'deleteEvents', 'manageEventRosters']}><LazyRoute><AdminEventsPage /></LazyRoute></PermissionGuard> },
              { path: "events/edit", element: <PermissionGuard anyOf={['createEvents', 'editEvents']}><LazyRoute><AdminEventEditPage /></LazyRoute></PermissionGuard> },
              { path: "events/checkin", element: <PermissionGuard anyOf={['checkInMembers', 'manualCheckIn']}><LazyRoute><AdminCheckInPage /></LazyRoute></PermissionGuard> },
              { path: "bostons", element: <PermissionGuard anyOf={['recordBostons', 'deleteBostons']}><LazyRoute><AdminBostonsPage /></LazyRoute></PermissionGuard> },
              { path: "leaderboard", element: <PermissionGuard anyOf={['recordMatches', 'editMatches', 'recordBostons']}><LazyRoute><AdminLeaderboardPage /></LazyRoute></PermissionGuard> },
              { path: "matches", element: <PermissionGuard anyOf={['recordMatches', 'editMatches', 'deleteMatches']}><LazyRoute><AdminMatchesListPage /></LazyRoute></PermissionGuard> },
              { path: "matches/:id", element: <PermissionGuard anyOf={['recordMatches', 'editMatches']}><LazyRoute><AdminMatchDetailPage /></LazyRoute></PermissionGuard> },
              { path: "matches/new", element: <PermissionGuard anyOf={['recordMatches', 'editMatches']}><LazyRoute><RecordMatchPage /></LazyRoute></PermissionGuard> },
              { path: "matches/:id/edit", element: <PermissionGuard anyOf={['recordMatches', 'editMatches']}><LazyRoute><RecordMatchPage /></LazyRoute></PermissionGuard> },
              { path: "tournaments", element: <PermissionGuard permission="manageTournaments"><LazyRoute><AdminTournamentsPage /></LazyRoute></PermissionGuard> },
              { path: "packages", element: <PermissionGuard permission="managePointsPackages"><LazyRoute><AdminPackagesPage /></LazyRoute></PermissionGuard> },
              { path: "packages/edit", element: <PermissionGuard permission="managePointsPackages"><LazyRoute><AdminPackageEditPage /></LazyRoute></PermissionGuard> },
              { path: "members", element: <PermissionGuard anyOf={['viewAdminDirectory', 'editMemberProfiles', 'managePaidMemberships', 'adjustLoyaltyPoints', 'awardAchievements', 'moderateAccounts']}><LazyRoute><AdminMembersPage /></LazyRoute></PermissionGuard> },
              { path: "members/edit", element: <PermissionGuard anyOf={['viewAdminDirectory', 'editMemberProfiles', 'managePaidMemberships', 'adjustLoyaltyPoints', 'awardAchievements', 'moderateAccounts']}><LazyRoute><AdminMemberEditPage /></LazyRoute></PermissionGuard> },
              { path: "venues", element: <PermissionGuard permission="manageVenues"><LazyRoute><AdminVenuesPage /></LazyRoute></PermissionGuard> },
              { path: "venues/edit", element: <PermissionGuard permission="manageVenues"><LazyRoute><AdminVenueEditPage /></LazyRoute></PermissionGuard> },
              { path: "system", element: <PermissionGuard anyOf={['viewAuditLogs', 'manageSystemTools']}><LazyRoute><AdminSystemPage /></LazyRoute></PermissionGuard> },
              { path: "memberships", element: <PermissionGuard permission="manageMembershipTiers"><LazyRoute><AdminMembershipsPage /></LazyRoute></PermissionGuard> },
              { path: "memberships/edit", element: <PermissionGuard permission="manageMembershipTiers"><LazyRoute><AdminMembershipEditPage /></LazyRoute></PermissionGuard> },
               { path: "reports", element: <PermissionGuard permission="viewFinancialReports"><LazyRoute><AdminReportsPage /></LazyRoute></PermissionGuard> },
              { path: "bot-flags", element: <PermissionGuard anyOf={['manageSystemTools', 'viewAuditLogs']}><LazyRoute><AdminBotFlagsPage /></LazyRoute></PermissionGuard> },
              { path: "feature-popups", element: <PermissionGuard anyOf={['manageSystemTools', 'createEvents', 'editEvents', 'editMemberProfiles']}><LazyRoute><AdminFeaturePopupsPage /></LazyRoute></PermissionGuard> },
              { path: "feature-popups/edit", element: <PermissionGuard anyOf={['manageSystemTools', 'createEvents', 'editEvents', 'editMemberProfiles']}><LazyRoute><AdminFeaturePopupEditPage /></LazyRoute></PermissionGuard> },
              { path: "games", element: <PermissionGuard anyOf={['manageSystemTools', 'viewAuditLogs']}><LazyRoute><AdminGamesPage /></LazyRoute></PermissionGuard> }
            ]
          }
        ]
      }
    ]
  },
  {
    path: "*",
    element: (
      <div style={{color: 'white', padding: '100px', textAlign: 'center', background: '#0a0d14', minHeight: '100vh'}}>
        <h1>Page Not Found</h1>
        <button onClick={() => window.location.href = '/'} style={{color: 'red', marginTop: '20px', cursor: 'pointer', background: 'transparent', border: '1px solid red', padding: '10px 20px', borderRadius: '8px'}}>Back to Home</button>
      </div>
    )
  }
    ]
  }
];

export const router = typeof document !== 'undefined'
  ? createBrowserRouter(routes)
  : createMemoryRouter(routes, { initialEntries: ['/'] });
